// File: frontend/src/components/Users/UsersList.jsx
// Users list component with filters and actions

import React, { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  EyeIcon, 
  PencilIcon, 
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';
import { updateUserStatus, deleteUser } from '../../services/api';
import { useMutation } from 'react-query';

const UsersList = ({ 
  users, 
  roles, 
  filters, 
  onFilterChange, 
  onViewUser, 
  onEditUser, 
  onCreateUser,
  onRefresh 
}) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Mutations for user operations
  const updateStatusMutation = useMutation(
    ({ id, isActive }) => updateUserStatus(id, isActive),
    {
      onSuccess: () => {
        onRefresh();
      },
      onError: (error) => {
        console.error('Error updating user status:', error);
        alert('Erro ao atualizar status do usuário');
      }
    }
  );

  const deleteUserMutation = useMutation(
    (id) => deleteUser(id),
    {
      onSuccess: () => {
        onRefresh();
      },
      onError: (error) => {
        console.error('Error deleting user:', error);
        alert('Erro ao excluir usuário');
      }
    }
  );

  const handleStatusToggle = (user) => {
    if (window.confirm(
      `Tem certeza que deseja ${user.is_active ? 'desativar' : 'ativar'} o usuário ${user.name}?`
    )) {
      updateStatusMutation.mutate({
        id: user.id,
        isActive: !user.is_active
      });
    }
  };

  const handleDelete = (user) => {
    if (window.confirm(
      `Tem certeza que deseja excluir o usuário ${user.name}?\n\nEsta ação não pode ser desfeita.`
    )) {
      deleteUserMutation.mutate(user.id);
    }
  };

  const getRoleBadgeColor = (roleName) => {
    const colors = {
      'Super_Admin': 'bg-red-100 text-red-800 border-red-200',
      'Admin_Marketing': 'bg-purple-100 text-purple-800 border-purple-200',
      'Marketing_Digital': 'bg-blue-100 text-blue-800 border-blue-200',
      'Especialista_Leads': 'bg-green-100 text-green-800 border-green-200',
      'Gerente_Vendas': 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };
    return colors[roleName] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header with filters */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Lista de Usuários ({users.length})
            </h3>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar usuários..."
                value={filters.search || ''}
                onChange={(e) => onFilterChange({ search: e.target.value })}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Filters Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium transition-colors ${
                showFilters ? 'bg-blue-50 text-blue-700 border-blue-300' : 'text-gray-700 bg-white hover:bg-gray-50'
              }`}
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filtros
            </button>

            {/* Refresh */}
            <button
              onClick={onRefresh}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Atualizar
            </button>

            {/* New User */}
            <button
              onClick={onCreateUser}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              <UserPlusIcon className="h-4 w-4 mr-2" />
              Novo Usuário
            </button>
          </div>
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={filters.active || 'true'}
                  onChange={(e) => onFilterChange({ active: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="true">Apenas Ativos</option>
                  <option value="false">Apenas Inativos</option>
                  <option value="all">Todos</option>
                </select>
              </div>

              {/* Role Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Perfil de Acesso
                </label>
                <select
                  value={filters.role || ''}
                  onChange={(e) => onFilterChange({ role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todos os Perfis</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.name}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Clear Filters */}
              <div className="flex items-end">
                <button
                  onClick={() => onFilterChange({ search: '', role: '', active: 'true' })}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Usuário
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Perfil de Acesso
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Criado em
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center">
                    <UserPlusIcon className="h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-500 text-lg">Nenhum usuário encontrado</p>
                    <p className="text-gray-400 text-sm mt-2">
                      {filters.search || filters.role || filters.active !== 'true' 
                        ? 'Tente ajustar os filtros ou criar um novo usuário'
                        : 'Comece criando o primeiro usuário do sistema'
                      }
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {user.first_name?.[0]}{user.last_name?.[0]}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getRoleBadgeColor(user.role_name)}`}>
                      {user.role_name}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleStatusToggle(user)}
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border transition-colors ${
                        user.is_active
                          ? 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200'
                          : 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200'
                      }`}
                    >
                      {user.is_active ? 'Ativo' : 'Inativo'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {format(new Date(user.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => onViewUser(user)}
                        className="text-blue-600 hover:text-blue-900 transition-colors"
                        title="Ver detalhes"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onEditUser(user)}
                        className="text-yellow-600 hover:text-yellow-900 transition-colors"
                        title="Editar usuário"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(user)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                        title="Excluir usuário"
                        disabled={deleteUserMutation.isLoading}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsersList;